/**
 * Rodzina DOCUMENTS, organ LexShift — dwa dokumenty A4, które wychodzą
 * z platformy do BIURA RACHUNKOWEGO (etap C planu
 * `docs/02-ARCHITECTURE/SALDEO_KADRY_PLAN.md`): ewidencja czasu pracy jednego
 * pracownika za miesiąc i grafik miesiąca całej załogi.
 *
 * Dlaczego to są wzory katalogu, a nie HTML sklejany w `KadrySync`: dokładnie
 * z tego samego powodu, co w poczcie. Ewidencja jest dokumentem, który trafia
 * do dokumentacji pracowniczej i zostaje tam na lata — treść składana w
 * miejscu wysyłki rozjeżdża się z podglądem w powłoce przy pierwszej poprawce,
 * a rozjazd widać dopiero na papierze u księgowej.
 *
 * LICZBY SĄ GOTOWE NA WEJŚCIU. Wzór nie liczy nadgodzin, normy ani salda —
 * robi to `zbudujEwidencjeMiesiaca()` w nexus-core, bo te same liczby idą
 * jeszcze do CSV zestawienia i do opisu dokumentu w Saldeo. Wzór, który
 * liczyłby cokolwiek u siebie, byłby DRUGĄ odpowiedzią na to samo pytanie.
 *
 * WIERSZ LISTY MOŻE BYĆ TEKSTEM. `dni` i `wiersze` to zmienne typu `list`,
 * a wołający przez REST bywa niedokładny (albo bramka podstawia wartownika).
 * Element, który nie jest obiektem, drukuje się jako jedna linia zamiast
 * znikać — brak wiersza w ewidencji jest niewidoczny z definicji, a właśnie
 * dlatego kosztowny.
 */

import { kompozycja, type PrintNode } from '../ir';
import { list, s, type PrintTemplateDef, type PrintVars } from '../types';

const A4 = { kind: 'document', page: 'A4' } as const;

/** Szerokość obszaru zadruku A4 przy marginesie 12 mm (`MARGIN_MM.A4`). */
const SZEROKOSC_MM = 186;

/** Pole wiersza listy; wiersz-tekst nie ma pól, więc oddaje pusty ciąg. */
function pole(wiersz: unknown, klucz: string): string {
  if (typeof wiersz !== 'object' || wiersz === null) return '';
  const wartosc = (wiersz as Record<string, unknown>)[klucz];
  return wartosc === null || wartosc === undefined ? '' : String(wartosc);
}

/** Wiersz przekazany jako tekst (nie obiekt) — drukujemy go w całości. */
function wierszTekstem(wiersz: unknown): string | null {
  return typeof wiersz === 'object' && wiersz !== null ? null : String(wiersz ?? '');
}

// ---------------------------------------------------------------------------
// Ewidencja czasu pracy — jeden pracownik, jeden miesiąc
// ---------------------------------------------------------------------------

/** Kolumny tabeli dni: [x, szerokość, nagłówek, klucz pola]. */
const KOLUMNY_DNI: [number, number, string, string][] = [
  [0, 14, 'Data', 'data'],
  [15, 9, 'Dz.', 'dzien'],
  [25, 22, 'Plan', 'plan'],
  [48, 32, 'Wejście–wyjście', 'odbicia'],
  [81, 14, 'Praca', 'praca'],
  [96, 14, 'Nadg.', 'nadgodziny'],
  [111, 12, 'Noc', 'noc'],
  [124, 10, 'Kod', 'kod'],
  [135, 51, 'Uwagi', 'uwagi'],
];

const Y_TABELA = 42;
const KROK_DNIA = 5.6;

function wierszeDni(v: PrintVars): PrintNode[][] {
  return list(v, 'dni').map((d) => {
    const tekstem = wierszTekstem(d);
    if (tekstem !== null) {
      return [
        { kind: 'text', xMm: 0, yMm: 0, text: tekstem, sizePt: 7, maxWidthMm: SZEROKOSC_MM },
      ] as PrintNode[];
    }
    return KOLUMNY_DNI.map(
      ([x, szerokosc, , klucz]) =>
        ({
          kind: 'text',
          xMm: x,
          yMm: 0,
          text: pole(d, klucz),
          sizePt: 7,
          maxWidthMm: szerokosc,
        }) as PrintNode
    );
  });
}

/**
 * Podsumowanie — dwie kolumny etykieta/wartość. Kolejność jest ta sama, co
 * w CSV zestawienia i w opisie dokumentu (`opisEwidencji`): jedna liczba
 * w trzech miejscach ma mieć jeden kształt i jedno miejsce w kolejce.
 */
function podsumowanie(v: PrintVars, y: number): PrintNode[] {
  const pary: [string, string][] = [
    ['Dni pracy', s(v, 'dni_pracy')],
    ['Godziny przepracowane', s(v, 'praca_h')],
    ['Norma miesiąca (art. 130 KP)', s(v, 'norma_h')],
    ['Nadgodziny dobowe', s(v, 'nadgodziny_dobowe_h')],
    ['Nadgodziny tygodniowe', s(v, 'nadgodziny_tygodniowe_h')],
    ['Saldo okresu rozliczeniowego', s(v, 'saldo_okresu_h')],
    ['Praca w porze nocnej (22–6)', s(v, 'noc_h')],
    ['Nieobecności', s(v, 'nieobecnosci')],
    ['Dni z niedomkniętym odbiciem', s(v, 'dni_niedomkniete')],
  ];
  const wezly: PrintNode[] = [
    { kind: 'line', xMm: 0, yMm: y - 3, x2Mm: SZEROKOSC_MM, y2Mm: y - 3, thicknessMm: 0.4 },
    { kind: 'text', xMm: 0, yMm: y, text: 'PODSUMOWANIE MIESIĄCA', sizePt: 9, bold: true },
  ];
  pary.forEach(([etykieta, wartosc], i) => {
    const wiersz = y + 7 + i * 4.4;
    wezly.push(
      { kind: 'text', xMm: 0, yMm: wiersz, text: etykieta, sizePt: 8, maxWidthMm: 70 },
      { kind: 'text', xMm: 72, yMm: wiersz, text: wartosc, sizePt: 8, bold: true, maxWidthMm: 60 }
    );
  });
  return wezly;
}

/**
 * Legenda kodów. Wypisana W CAŁOŚCI, nie tylko dla kodów użytych w tym
 * miesiącu: dokument czyta człowiek, który porównuje go z ewidencją innego
 * pracownika, i pytanie „co znaczy UO" nie może zależeć od tego, czy ktoś
 * akurat brał urlop okolicznościowy.
 */
const LEGENDA =
  'UW — urlop wypoczynkowy · CH — niezdolność do pracy (chorobowe) · ' +
  'UB — urlop bezpłatny · UR — urlop rodzicielski · OP — opieka · ' +
  'UO — urlop okolicznościowy · IN — inna nieobecność usprawiedliwiona · ' +
  'NN — nieobecność nieusprawiedliwiona · W — dzień wolny z rozkładu · ' +
  'WN — niedziela · WŚ — święto';

const ewidencjaCzasuPracy: PrintTemplateDef = {
  slug: 'lexshift.ewidencja_czasu_pracy',
  name: 'Ewidencja czasu pracy — miesiąc',
  family: 'documents',
  organ: 'LexShift',
  trigger:
    'Miesięczna wysyłka dokumentów kadrowych do systemu biura (KadrySync, zakres „ewidencja") oraz przycisk „Wyślij ewidencję za miesiąc" w powłoce.',
  media: A4,
  targets: ['PDF'],
  version: 1,
  variables: [
    {
      name: 'firma',
      description: 'Nazwa pracodawcy z ustawień LexShift najemcy.',
      example: 'Piekarnia Zaczyn sp. z o.o.',
    },
    {
      name: 'miesiac',
      description: 'Miesiąc ewidencji słownie z rokiem; sufiks korekty wchodzi tutaj.',
      example: 'sierpień 2026',
    },
    {
      name: 'pracownik',
      description: 'Imię i nazwisko pracownika.',
      example: 'Anna Nowak-Kowalska',
    },
    {
      name: 'akronim',
      description: 'Akronim z mapy tożsamości — klucz naturalny po stronie systemu biura.',
      example: 'NOWAK-ANNA',
    },
    {
      name: 'stanowisko',
      description: 'Stanowisko podstawowe; pracownik bez przypisania go nie ma.',
      example: 'Piekarz',
      optional: true,
    },
    {
      name: 'etat_h_tydz',
      description: 'Tygodniowy wymiar etatu w godzinach (pełny etat = 40).',
      example: 40,
      kind: 'number',
    },
    {
      name: 'dni',
      description:
        'Dni miesiąca po kolei: data, dzien, plan, odbicia, praca, nadgodziny, noc, kod, uwagi.',
      example: [
        {
          data: '03.08.2026',
          dzien: 'pon',
          plan: '06:00–14:00',
          odbicia: '05:58–14:07',
          praca: '7:39',
          nadgodziny: '0:00',
          noc: '0:02',
          kod: '',
          uwagi: '',
        },
        {
          data: '15.08.2026',
          dzien: 'sob',
          plan: '',
          odbicia: '',
          praca: '0:00',
          nadgodziny: '0:00',
          noc: '0:00',
          kod: 'WŚ',
          uwagi: 'Wniebowzięcie NMP',
        },
      ],
      kind: 'list',
    },
    { name: 'dni_pracy', description: 'Liczba dni z pracą.', example: 21, kind: 'number' },
    { name: 'praca_h', description: 'Suma godzin pracy, `H:MM`.', example: '168:30' },
    { name: 'norma_h', description: 'Wymiar miesiąca z art. 130 KP, `H:MM`.', example: '176:00' },
    {
      name: 'nadgodziny_dobowe_h',
      description: 'Nadwyżki ponad dobowy próg najemcy, `H:MM`.',
      example: '6:00',
    },
    {
      name: 'nadgodziny_tygodniowe_h',
      description: 'Nadgodziny tygodni zakończonych w miesiącu, `H:MM`.',
      example: '2:00',
    },
    {
      name: 'saldo_okresu_h',
      description: 'Przepracowane − norma narastająco do końca miesiąca; ze znakiem.',
      example: '-7:30',
    },
    { name: 'noc_h', description: 'Praca w porze nocnej 22–6, `H:MM`.', example: '12:00' },
    {
      name: 'nieobecnosci',
      description: 'Nieobecności per kod, jedno zdanie; „brak", gdy żadnej nie było.',
      example: 'UW 2 dni, CH 1 dni',
    },
    {
      name: 'dni_niedomkniete',
      description: 'Dni z odbiciem bez wyjścia — godzin z nich NIE liczymy.',
      // Przykład JEDNOCYFROWY zasiew układu pomija świadomie (wartości
      // krótsze niż dwa znaki trafiają się przypadkiem w środku każdego
      // innego napisu), więc wiązanie nie powstawało i bramka to złapała.
      example: 19,
      kind: 'number',
    },
    {
      name: 'wygenerowano',
      description: 'Data i godzina wygenerowania dokumentu.',
      example: '03.09.2026, 06:12',
      kind: 'date',
    },
  ],
  build: (v) =>
    kompozycja(A4, [
      {
        kind: 'text',
        xMm: 0,
        yMm: 0,
        text: s(v, 'firma'),
        sizePt: 11,
        bold: true,
        maxWidthMm: 120,
      },
      {
        kind: 'text',
        xMm: 0,
        yMm: 8,
        text: `EWIDENCJA CZASU PRACY — ${s(v, 'miesiac')}`,
        sizePt: 14,
        bold: true,
        maxWidthMm: SZEROKOSC_MM,
      },
      {
        kind: 'text',
        xMm: 0,
        yMm: 18,
        text: `${s(v, 'pracownik')}  ·  ${s(v, 'akronim')}`,
        sizePt: 10,
        maxWidthMm: 120,
      },
      {
        kind: 'text',
        xMm: 0,
        yMm: 24,
        text: `Stanowisko: ${s(v, 'stanowisko', '—')}  ·  Wymiar etatu: ${s(v, 'etat_h_tydz')} h/tydz.`,
        sizePt: 9,
        maxWidthMm: SZEROKOSC_MM,
      },
      { kind: 'line', xMm: 0, yMm: 32, x2Mm: SZEROKOSC_MM, y2Mm: 32, thicknessMm: 0.4 },

      // Nagłówek tabeli dni.
      ...KOLUMNY_DNI.map(
        ([x, szerokosc, naglowek]) =>
          ({
            kind: 'text',
            xMm: x,
            yMm: Y_TABELA - 6,
            text: naglowek,
            sizePt: 7,
            bold: true,
            maxWidthMm: szerokosc,
          }) as PrintNode
      ),
      {
        kind: 'line',
        xMm: 0,
        yMm: Y_TABELA - 1.5,
        x2Mm: SZEROKOSC_MM,
        y2Mm: Y_TABELA - 1.5,
        thicknessMm: 0.3,
      },
      { kind: 'repeat', yMm: Y_TABELA, stepMm: KROK_DNIA, rows: wierszeDni(v) },

      ...podsumowanie(v, 218),
      // Legenda stoi OBOK podsumowania, nie pod nim: obszar zadruku A4 przy
      // marginesie 12 mm ma 273 mm wysokości, a tabela 31 dni plus dziewięć
      // wierszy podsumowania zjada 260. Blok postawiony niżej zaczynał się
      // poza mediami — złapał to walidator układu, nie oko.
      { kind: 'text', xMm: 138, yMm: 218, text: LEGENDA, sizePt: 5, maxWidthMm: 48 },
      {
        kind: 'text',
        xMm: 0,
        yMm: 266,
        text: `Wygenerowano z Insolitum Universe ${s(v, 'wygenerowano')}. Ewidencja prowadzona elektronicznie (§ 6 pkt 1 rozporządzenia o dokumentacji pracowniczej); odbicia pochodzą z kiosku i aplikacji pracownika.`,
        sizePt: 6,
        maxWidthMm: SZEROKOSC_MM,
      },
    ]),
};

// ---------------------------------------------------------------------------
// Grafik miesiąca — siatka pracownicy × dni
// ---------------------------------------------------------------------------

const X_NAZWISKO = 0;
const SZEROKOSC_NAZWISKA = 38;
const X_PIERWSZY_DZIEN = 40;
const KROK_KOLUMNY = 4.7;
const Y_SIATKA = 38;
const KROK_WIERSZA = 5.2;

/**
 * Wiersz siatki: nazwisko, skróty zmian dzień po dniu, suma godzin planu.
 * `skroty` jest LISTĄ, nie sklejonym tekstem — inaczej dzień bez zmiany
 * musiałby być rozpoznawany po szerokości pustego miejsca, a dwuznakowy
 * skrót obok jednoznakowego przesunąłby całą resztę wiersza.
 */
function wierszeGrafiku(v: PrintVars, dniWMiesiacu: number): PrintNode[][] {
  return list(v, 'wiersze').map((w) => {
    const tekstem = wierszTekstem(w);
    if (tekstem !== null) {
      return [
        { kind: 'text', xMm: 0, yMm: 0, text: tekstem, sizePt: 6, maxWidthMm: SZEROKOSC_MM },
      ] as PrintNode[];
    }
    const skroty = Array.isArray((w as Record<string, unknown>)['skroty'])
      ? ((w as Record<string, unknown>)['skroty'] as unknown[])
      : [];
    const komorki: PrintNode[] = [
      {
        kind: 'text',
        xMm: X_NAZWISKO,
        yMm: 0,
        text: pole(w, 'pracownik'),
        sizePt: 6,
        maxWidthMm: SZEROKOSC_NAZWISKA,
      },
    ];
    for (let dzien = 0; dzien < dniWMiesiacu; dzien += 1) {
      const skrot = skroty[dzien];
      if (skrot === null || skrot === undefined || skrot === '') continue;
      komorki.push({
        kind: 'text',
        xMm: X_PIERWSZY_DZIEN + dzien * KROK_KOLUMNY,
        yMm: 0,
        text: String(skrot),
        sizePt: 5,
        maxWidthMm: KROK_KOLUMNY,
        align: 'center',
      });
    }
    komorki.push({
      kind: 'text',
      xMm: X_PIERWSZY_DZIEN + dniWMiesiacu * KROK_KOLUMNY + 1,
      yMm: 0,
      text: pole(w, 'suma_h'),
      sizePt: 6,
      bold: true,
      maxWidthMm: 14,
    });
    return komorki;
  });
}

const grafikMiesiaca: PrintTemplateDef = {
  slug: 'lexshift.grafik_miesiaca',
  name: 'Grafik miesiąca — siatka załogi',
  family: 'documents',
  organ: 'LexShift',
  trigger:
    'Miesięczna wysyłka dokumentów kadrowych do systemu biura — tylko gdy grafik miesiąca został opublikowany.',
  media: A4,
  targets: ['PDF'],
  version: 1,
  variables: [
    {
      name: 'firma',
      description: 'Nazwa pracodawcy z ustawień LexShift najemcy.',
      example: 'Piekarnia Zaczyn sp. z o.o.',
    },
    { name: 'miesiac', description: 'Miesiąc grafiku słownie z rokiem.', example: 'sierpień 2026' },
    {
      name: 'dni',
      description:
        'Nagłówki kolumn dni, po kolei — numer dnia, z kropką przy niedzieli i święcie („7·"). Długość listy wyznacza liczbę kolumn.',
      example: ['1', '2', '3', '4', '5', '6', '7·'],
      kind: 'list',
    },
    {
      name: 'wiersze',
      description:
        'Pracownicy: pracownik, skroty (lista skrótów zmian dzień po dniu, puste = wolne), suma_h.',
      example: [
        {
          pracownik: 'Anna Nowak-Kowalska',
          skroty: ['R', 'R', 'R', '', '', 'P', 'P'],
          suma_h: '168:00',
        },
      ],
      kind: 'list',
    },
    {
      name: 'zmian',
      description: 'Liczba zaplanowanych zmian w miesiącu.',
      example: 412,
      kind: 'number',
    },
    {
      name: 'opublikowano',
      description: 'Data publikacji grafiku; bez publikacji dokument nie powstaje.',
      example: '28.07.2026',
      optional: true,
    },
    {
      name: 'wygenerowano',
      description: 'Data i godzina wygenerowania dokumentu.',
      example: '03.09.2026, 06:12',
      kind: 'date',
    },
  ],
  build: (v) => {
    // Liczbę kolumn wyznacza DŁUGOŚĆ listy nagłówków, a nie osobna liczba:
    // dwie wartości mówiące to samo rozjeżdżają się przy pierwszym lutym.
    // Przycięcie do 31 jest zaporą na dane spoza kalendarza — kolumna 32
    // wyjechałaby poza obszar zadruku i zniknęła bez śladu.
    const naglowki = list(v, 'dni').slice(0, 31);
    const dni = naglowki.length;
    const naglowkiDni: PrintNode[] = naglowki.map(
      (etykieta, i) =>
        ({
          kind: 'text',
          xMm: X_PIERWSZY_DZIEN + i * KROK_KOLUMNY,
          yMm: Y_SIATKA - 5,
          text: String(etykieta ?? ''),
          sizePt: 5,
          bold: true,
          maxWidthMm: KROK_KOLUMNY,
          align: 'center',
        }) as PrintNode
    );
    return kompozycja(A4, [
      {
        kind: 'text',
        xMm: 0,
        yMm: 0,
        text: s(v, 'firma'),
        sizePt: 11,
        bold: true,
        maxWidthMm: 120,
      },
      {
        kind: 'text',
        xMm: 0,
        yMm: 8,
        text: `GRAFIK PRACY — ${s(v, 'miesiac')}`,
        sizePt: 14,
        bold: true,
        maxWidthMm: SZEROKOSC_MM,
      },
      {
        kind: 'text',
        xMm: 0,
        yMm: 18,
        text: `Zaplanowanych zmian: ${s(v, 'zmian')}${
          s(v, 'opublikowano') ? `  ·  opublikowany ${s(v, 'opublikowano')}` : ''
        }`,
        sizePt: 9,
        maxWidthMm: SZEROKOSC_MM,
      },
      { kind: 'line', xMm: 0, yMm: 26, x2Mm: SZEROKOSC_MM, y2Mm: 26, thicknessMm: 0.4 },
      {
        kind: 'text',
        xMm: X_NAZWISKO,
        yMm: Y_SIATKA - 5,
        text: 'Pracownik',
        sizePt: 6,
        bold: true,
        maxWidthMm: SZEROKOSC_NAZWISKA,
      },
      ...naglowkiDni,
      {
        kind: 'text',
        xMm: X_PIERWSZY_DZIEN + dni * KROK_KOLUMNY + 1,
        yMm: Y_SIATKA - 5,
        text: 'Razem',
        sizePt: 6,
        bold: true,
        maxWidthMm: 14,
      },
      {
        kind: 'line',
        xMm: 0,
        yMm: Y_SIATKA - 1.5,
        x2Mm: SZEROKOSC_MM,
        y2Mm: Y_SIATKA - 1.5,
        thicknessMm: 0.3,
      },
      { kind: 'repeat', yMm: Y_SIATKA, stepMm: KROK_WIERSZA, rows: wierszeGrafiku(v, dni) },
      {
        kind: 'text',
        xMm: 0,
        yMm: 266,
        text: `Wygenerowano z Insolitum Universe ${s(v, 'wygenerowano')}. Skróty zmian pochodzą z kartoteki zmian LexShift; pusta komórka znaczy dzień bez zaplanowanej zmiany.`,
        sizePt: 6,
        maxWidthMm: SZEROKOSC_MM,
      },
    ]);
  },
};

export const lexshiftTemplates: PrintTemplateDef[] = [ewidencjaCzasuPracy, grafikMiesiaca];
